import { ApiError, billing, unwrap } from "../api";
import { formatDate, formatMoney } from "../format";
import { creditNotePath, invoicePath, isGuid, redirectToCanonical } from "../paths";
import type { ComplianceState } from "@steelyard/theme";
import type { BillingComponents } from "@steelyard/api-client";
import type { PagedResult } from "../../components/table/data-table";

/**
 * Ported from apps/admin/app/(app)/invoicing/invoicing.ts + actions.ts, against the same gateway
 * paths (checked in apps/admin/lib/api.ts's `billing()`/`billingWritable()` client — one gateway
 * prefix, `/api/billing/...`, for invoicing/credit-notes/series/audit). Nothing here computes; it
 * parses what the API sent and renders it (constitution I).
 */

export type InvoiceRow = {
  id: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  invoiceDate: string;
  grandTotal: number;
  state: string;
  irnStatus: string;
  tallySyncStatus: string;
  invoiceBasis: string;
  /** §17: the e-way bill's status as Billing recorded it (`generated`, `failed`, …); `null` when none was attempted. */
  ewayBillStatus: string | null;
  /** D-40: raised but held for a Super Admin's review — not yet registered, in Tally or on the ledger. */
  reviewPending: boolean;
  /** The source order, as Billing recorded it; null when the row carries none. */
  orderId: string | null;
  /** Ordering's number for it; null when Ordering could not say. */
  orderNumber: string | null;
};

const text = (value: unknown): string | null => (typeof value === "string" ? value : null);

const decimal = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** ₹12,34,567.89 from a figure the API already rounded. */
export function money(value: unknown): string {
  return formatMoney(decimal(value));
}

function parseRow(raw: Record<string, unknown>): InvoiceRow {
  const id = text(raw.id);
  const invoiceNumber = text(raw.invoiceNumber);
  const customerName = text(raw.customerName);
  const invoiceDate = text(raw.invoiceDate);
  const state = text(raw.state);
  const irnStatus = text(raw.irnStatus);
  const tallySyncStatus = text(raw.tallySyncStatus);
  const grandTotal = decimal(raw.grandTotal);

  if (id === null || invoiceNumber === null || customerName === null || invoiceDate === null || state === null || irnStatus === null || tallySyncStatus === null || grandTotal === null) {
    throw new Error(
      "GET /api/billing/admin/invoices answered with a row this screen cannot read. The operation " +
        "declares no response schema, so the shape is checked here rather than by the compiler; a " +
        "row missing its number, customer, date, total or compliance statuses is a contract change, " +
        "not a row to render blank (constitution VI).",
    );
  }

  return {
    id,
    invoiceNumber,
    customerId: text(raw.customerId) ?? "",
    customerName,
    invoiceDate,
    grandTotal,
    state,
    irnStatus,
    tallySyncStatus,
    invoiceBasis: text(raw.invoiceBasis) ?? "",
    ewayBillStatus: text(raw.ewayBillStatus),
    reviewPending: raw.reviewPending === true,
    orderId: text(raw.orderId),
    orderNumber: text(raw.orderNumber),
  };
}

function parseInvoicePage(body: unknown): PagedResult<InvoiceRow> {
  const raw = (body ?? {}) as Record<string, unknown>;
  const items = raw.items;
  const page = decimal(raw.page);
  const pageSize = decimal(raw.pageSize);
  const totalCount = decimal(raw.totalCount);

  if (!Array.isArray(items) || page === null || pageSize === null || totalCount === null) {
    throw new Error(
      "GET /api/billing/admin/invoices answered without a paged envelope (items, page, pageSize, " +
        "totalCount). Nothing is inferred from a partial one.",
    );
  }

  return {
    items: items.map((item) => parseRow((item ?? {}) as Record<string, unknown>)),
    page,
    pageSize,
    totalCount,
  };
}

/** Billing's `IrnStatus`, mapped onto ComplianceStatusBadge's five states (`011` R6, D-24). */
export const IRN_STATE: Readonly<Record<string, ComplianceState>> = {
  not_applicable: "not_applicable",
  not_configured: "pending",
  pending: "pending",
  registered: "done",
  failed: "failed",
  cancellation_requested: "pending",
  cancelled: "expired",
};

export const TALLY_STATE: Readonly<Record<string, ComplianceState>> = {
  not_queued: "not_applicable",
  retained: "pending",
  queued: "pending",
  posted: "done",
  failed: "failed",
};

export function statusWords(status: string): string {
  return status.replaceAll("_", " ");
}

const OFFERED_TO_TALLY = new Set(["retained", "queued", "posted", "failed"]);
const NO_IRN_YET = new Set(["not_configured", "pending", "failed"]);

/** BR-07's violation, rendered rather than normalised — never occurs in ordinary data. */
export function isBr07Defect(row: Pick<InvoiceRow, "irnStatus" | "tallySyncStatus">): boolean {
  return OFFERED_TO_TALLY.has(row.tallySyncStatus) && NO_IRN_YET.has(row.irnStatus);
}

export const BASIS_LABEL: Readonly<Record<string, string>> = {
  actual_measured_quantity: "Actual measured quantity at packing",
  actual_piece_count: "Actual piece count",
};

export const BASIS_RULE: Readonly<Record<string, string>> = {
  actual_measured_quantity:
    "BR-03 — a B2B invoice is raised on the quantity actually measured at packing, at the tier unit rate. The quantity invoiced is not the quantity ordered.",
  actual_piece_count:
    "BR-02 — a B2C invoice is raised on the actual piece count at the piece price. The weight recorded at packing is on the record and did not affect this amount.",
};

export function isCancelled(state: string): boolean {
  return state === "cancelled";
}

// ---- Wire types for the detail/audit/series responses (Billing declares these, unlike the list). ----

export type InvoiceLine = {
  id: string;
  skuName: string;
  brandName: string;
  categoryName: string;
  hsnCode: string;
  gstRate: number | string;
  quantity: number | string;
  unitCode: string;
  rate: number;
  taxableValue: number;
  /** Billing's per-line tax heads — printed as sent, never split from `taxAmount` here. */
  cgst: number;
  sgst: number;
  igst: number;
  taxAmount: number;
  lineTotalDisplay: string;
  /** D-39: quantity × rate before the trade discount; equals `taxableValue` on a line with none. */
  grossAmount: number;
  /** D-39: the order's trade discount percentage, copied onto the line. 0 when none. */
  discountPercent: number;
  /** D-39: the line's trade discount. `grossAmount − discount = taxableValue`, as Billing stored it. */
  discount: number;
};

export type InvoiceDetail = {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  financialYear: string;
  supplierGstin: string | null;
  supplierStateCode: string;
  recipientLegalName: string;
  recipientGstin: string | null;
  recipientAddress: string;
  placeOfSupply: string;
  state: string;
  irnStatus: string;
  irn: string | null;
  /** Present on both `GetInvoice` and `PrintInvoice` — same `InvoicePrintHandler.Render`. */
  irnQr: string | null;
  /** The company's name/address and the e-way bill, as the API sent them (R7) — absent on older invoices. */
  supplierLegalName: string | null;
  supplierAddress: string | null;
  ewayBillNo: string | null;
  ewayBillStatus: string | null;
  ewayBillDate: string | null;
  ewayBillValidUntil: string | null;
  invoiceBasis: string;
  taxHead: string;
  lines: InvoiceLine[];
  /** D-39: the lines' gross before the trade discount. */
  grossValue: number;
  /** D-39: the trade discount, already taken off before GST. 0 when none. */
  discount: number;
  /** D-31: the order's delivery charge, invoiced untaxed. 0 when none (and on invoices raised before it was invoiced). */
  deliveryCharge: number;
  taxableValue: number;
  cgst: number;
  sgst: number;
  igst: number;
  roundOff: number;
  grandTotal: number;
  grandTotalDisplay: string;
  /** D-40: held for a Super Admin's review; its discount and round off may still change. */
  reviewPending: boolean;
  orderId: string | null;
  orderNumber: string | null;
};

/**
 * `InvoicePrint`, validated at the boundary the way `parseRow`/`parseInvoicePage` already validate
 * the list (constitution VI) — `PrintInvoice`'s generated type is trusted for shape, but a field
 * missing at runtime is a contract change, not a row to print blank.
 */
function parseInvoicePrint(body: unknown, route: string): InvoiceDetail {
  const raw = (body ?? {}) as Record<string, unknown>;
  const id = text(raw.id);
  const invoiceNumber = text(raw.invoiceNumber);
  const invoiceDate = text(raw.invoiceDate);
  const financialYear = text(raw.financialYear);
  const recipientLegalName = text(raw.recipientLegalName);
  const recipientAddress = text(raw.recipientAddress);
  const placeOfSupply = text(raw.placeOfSupply);
  const state = text(raw.state);
  const irnStatus = text(raw.irnStatus);
  const invoiceBasis = text(raw.invoiceBasis);
  const taxHead = text(raw.taxHead);
  const grandTotalDisplay = text(raw.grandTotalDisplay);
  const lines = raw.lines;

  if (
    id === null ||
    invoiceNumber === null ||
    invoiceDate === null ||
    financialYear === null ||
    recipientLegalName === null ||
    recipientAddress === null ||
    placeOfSupply === null ||
    state === null ||
    irnStatus === null ||
    invoiceBasis === null ||
    taxHead === null ||
    grandTotalDisplay === null ||
    !Array.isArray(lines)
  ) {
    throw new Error(
      `GET ${route} answered with a body this screen cannot read ` +
        "(a required field or the lines array is missing). That is a contract change, not a document " +
        "to print half-empty (constitution VI).",
    );
  }

  return {
    id,
    invoiceNumber,
    invoiceDate,
    financialYear,
    supplierGstin: text(raw.supplierGstin),
    supplierStateCode: text(raw.supplierStateCode) ?? "",
    recipientLegalName,
    recipientGstin: text(raw.recipientGstin),
    recipientAddress,
    placeOfSupply,
    state,
    irnStatus,
    irn: text(raw.irn),
    irnQr: text(raw.irnQr),
    supplierLegalName: text(raw.supplierLegalName),
    supplierAddress: text(raw.supplierAddress),
    ewayBillNo: text(raw.ewayBillNo),
    ewayBillStatus: text(raw.ewayBillStatus),
    ewayBillDate: text(raw.ewayBillDate),
    ewayBillValidUntil: text(raw.ewayBillValidUntil),
    invoiceBasis,
    taxHead,
    lines: lines.map((line) => {
      const l = (line ?? {}) as Record<string, unknown>;
      return {
        id: text(l.id) ?? "",
        skuName: text(l.skuName) ?? "",
        brandName: text(l.brandName) ?? "",
        categoryName: text(l.categoryName) ?? "",
        hsnCode: text(l.hsnCode) ?? "",
        gstRate: (decimal(l.gstRate) ?? l.gstRate) as number | string,
        quantity: (decimal(l.quantity) ?? l.quantity) as number | string,
        unitCode: text(l.unitCode) ?? "",
        rate: decimal(l.rate) ?? 0,
        taxableValue: decimal(l.taxableValue) ?? 0,
        cgst: decimal(l.cgst) ?? 0,
        sgst: decimal(l.sgst) ?? 0,
        igst: decimal(l.igst) ?? 0,
        taxAmount: decimal(l.taxAmount) ?? 0,
        lineTotalDisplay: text(l.lineTotalDisplay) ?? "",
        // An invoice raised before D-39 has no discount, so its gross IS its taxable value — read
        // from the API when present (it always is from D-39 on), never derived here.
        grossAmount: decimal(l.grossAmount) ?? decimal(l.taxableValue) ?? 0,
        discountPercent: decimal(l.discountPercent) ?? 0,
        discount: decimal(l.discount) ?? 0,
      };
    }),
    grossValue: decimal(raw.grossValue) ?? decimal(raw.taxableValue) ?? 0,
    discount: decimal(raw.discount) ?? 0,
    deliveryCharge: decimal(raw.deliveryCharge) ?? 0,
    taxableValue: decimal(raw.taxableValue) ?? 0,
    cgst: decimal(raw.cgst) ?? 0,
    sgst: decimal(raw.sgst) ?? 0,
    igst: decimal(raw.igst) ?? 0,
    roundOff: decimal(raw.roundOff) ?? 0,
    grandTotal: decimal(raw.grandTotal) ?? 0,
    grandTotalDisplay,
    reviewPending: raw.reviewPending === true,
    orderId: text(raw.orderId),
    orderNumber: text(raw.orderNumber),
  };
}

export type AuditEntry = {
  id: string;
  occurredAt: string;
  action: string;
  actorId: string | null;
  /** Billing's name for `actorId`; null when it could not say. Never replaced by the id. */
  actorName?: string | null;
  actorComponent: string | null;
  reason: string | null;
};

export type InvoiceSeries = {
  id: string;
  seriesCode: string;
  financialYear: string;
  isActive: boolean;
  format: string;
  nextNumber: number | string;
  gaps: (number | string)[];
};

export type InvoiceListFilters = {
  page: number;
  pageSize?: number;
  state?: string;
  irn?: string;
  from?: string;
  to?: string;
  /** Server-side substring match (`InvoiceStore.SearchAsync`'s `.Contains`), not exact — callers
   * matching a single reference (T019's QueueDetail lookup) must still key off the exact string. */
  invoiceNumber?: string;
  /** D-40: `true` lists only the invoices held for review. */
  reviewPending?: boolean;
  /**
   * The list's one search box: invoices whose number contains this, OR on `orderIds` — the orders
   * Ordering matched by the same term (it owns order numbers; Billing only sees their ids).
   */
  search?: string;
  /** With `search`, its order half; without it, only these orders' invoices. */
  orderIds?: string[];
};

const PAGE_SIZE = 25;
/** Billing's own clamp (`AdminInvoiceController`) — the largest page any caller may request. */
export const MAX_INVOICE_PAGE_SIZE = 200;

export async function listInvoices(filters: InvoiceListFilters): Promise<PagedResult<InvoiceRow>> {
  return parseInvoicePage(
    await unwrap(
      (await billing()).GET("/api/billing/admin/invoices", {
        params: {
          query: {
            page: filters.page,
            pageSize: filters.pageSize ?? PAGE_SIZE,
            state: filters.state,
            irnStatus: filters.irn,
            from: filters.from,
            to: filters.to,
            invoiceNumber: filters.invoiceNumber,
            reviewPending: filters.reviewPending,
            orderId: filters.orderIds,
            search: filters.search,
          },
        },
      }),
    ),
  );
}

/** Same `InvoicePrint` body as `/print`, so validated by the same parser rather than cast. */
export async function getInvoice(id: string): Promise<InvoiceDetail> {
  return parseInvoicePrint(
    await unwrap((await billing()).GET("/api/billing/admin/invoices/{id}", { params: { path: { id } } })),
    "/api/billing/admin/invoices/{id}",
  );
}

/**
 * Billing's exact-match lookup — a query parameter, not a path segment, because a number may
 * contain `/`. 404 when none; 409 (a problem document) when two series render the same number.
 */
export async function getInvoiceByNumber(number: string): Promise<InvoiceDetail> {
  return parseInvoicePrint(
    await unwrap((await billing()).GET("/api/billing/admin/invoices/by-number", { params: { query: { number } } })),
    "/api/billing/admin/invoices/by-number",
  );
}

/** One record an ambiguous number could mean, linked by its id (an id URL stays put for it). */
export type NumberMatch = { href: string; label: string; detail: string };

/**
 * Billing's 409 for a number two series both render. Carries the problem as sent, and the records
 * it could mean — empty when they could not be listed. `NumberRouteError` renders it.
 */
export class AmbiguousNumberError extends Error {
  readonly problem: ApiError;
  readonly matches: readonly NumberMatch[];
  constructor(problem: ApiError, matches: readonly NumberMatch[]) {
    super(problem.message);
    this.problem = problem;
    this.matches = matches;
  }
}

const isConflict = (err: unknown): err is ApiError => err instanceof ApiError && err.status === 409;

/** True only when the number lookup answers 409. Any other failure keeps the redirect as it was. */
async function numberIsAmbiguous(lookup: () => Promise<unknown>): Promise<boolean> {
  try {
    await lookup();
    return false;
  } catch (err) {
    return isConflict(err);
  }
}

/**
 * The invoices an ambiguous number could mean: the list's `invoiceNumber` filter is a contains
 * match, so only exact (ordinal) matches are kept. Listing needs `billing.read`; a caller without
 * it (a Supervisor on dispatch) gets the problem alone.
 */
async function invoiceMatches(number: string, to: (key: string) => string): Promise<NumberMatch[]> {
  try {
    const page = await listInvoices({ page: 1, pageSize: MAX_INVOICE_PAGE_SIZE, invoiceNumber: number });
    return page.items
      .filter((row) => row.invoiceNumber === number)
      .map((row) => ({ href: to(row.id), label: row.invoiceNumber, detail: `${formatDate(row.invoiceDate)} · ${row.customerName}` }));
  } catch {
    return [];
  }
}

/**
 * The invoice a route's `:invoiceNumber` names. A legacy id throws a redirect to `to(number)` —
 * the invoice, or its dispatch screen — keeping `/audit`, `/print` and the query string; unless
 * that number is ambiguous, when the id URL is the only address that reaches this record and stays.
 * An ambiguous number throws `AmbiguousNumberError` listing what it could mean.
 */
export async function invoiceByKey(request: Request, key: string, to = invoicePath): Promise<InvoiceDetail> {
  if (!isGuid(key)) {
    try {
      return await getInvoiceByNumber(key);
    } catch (err) {
      if (isConflict(err)) throw new AmbiguousNumberError(err, await invoiceMatches(key, to));
      throw err;
    }
  }
  const invoice = await getInvoice(key);
  if (await numberIsAmbiguous(() => getInvoiceByNumber(invoice.invoiceNumber))) return invoice;
  throw redirectToCanonical(request, key, to(invoice.invoiceNumber));
}

/**
 * The print route's own read — deliberately `/print`, not `/{id}`, even though both answer the same
 * `InvoicePrint` shape (`InvoicePrintHandler.Render`): the real `/print` route also writes a
 * `Reprinted` audit row server-side on every call, which is the point of hitting it (FR-050).
 */
export async function getInvoicePrint(id: string): Promise<InvoiceDetail> {
  return parseInvoicePrint(
    await unwrap((await billing()).GET("/api/billing/admin/invoices/{id}/print", { params: { path: { id } } })),
    "/api/billing/admin/invoices/{id}/print",
  );
}

export async function getInvoiceAudit(id: string): Promise<AuditEntry[]> {
  return unwrap(
    (await billing()).GET("/api/billing/admin/invoices/{id}/audit", { params: { path: { id } } }),
  ) as Promise<AuditEntry[]>;
}

export async function listInvoiceSeries(): Promise<InvoiceSeries[]> {
  return unwrap((await billing()).GET("/api/billing/admin/invoice-series")) as Promise<InvoiceSeries[]>;
}

/**
 * The 409 Billing answers when the cancellation window has closed. Carries
 * `creditNoteRouteAvailable` and `windowClosedAt` — read defensively since the operation declares
 * no body for its 409.
 */
export function windowPassed(error: unknown): { closedAt: string | null } | null {
  // `unwrap` throws an ApiError, which keeps the service's extra fields on `.raw`.
  const problem = ((error instanceof ApiError ? error.raw : error) ?? {}) as Record<string, unknown>;
  if (problem.creditNoteRouteAvailable !== true) return null;
  const closedAt = problem.windowClosedAt;
  return { closedAt: typeof closedAt === "string" ? closedAt : null };
}

/**
 * D-40: a Super Admin's edit to an invoice in review. Billing re-prices every line and answers the
 * invoice as it now stands — every figure its own; this screen computes none of them.
 */
export async function reviseInvoice(
  id: string,
  revision: { discountMode: "none" | "percent" | "amount"; discountValue: number; roundOff: number | null; reason: string },
): Promise<InvoiceDetail> {
  return parseInvoicePrint(
    await unwrap(
      (await billing()).POST("/api/billing/admin/invoices/{id}/revise", { params: { path: { id } }, body: revision }),
    ),
    "/api/billing/admin/invoices/{id}/revise",
  );
}

/** D-40: ends the review — the invoice is registered or queued for Tally, and posted to the ledger. */
export async function finaliseInvoice(id: string): Promise<void> {
  await unwrap((await billing()).POST("/api/billing/admin/invoices/{id}/finalise", { params: { path: { id } } }));
}

/** D-41: a settlement recorded after payment, as Billing stored it. */
export type Settlement = BillingComponents["schemas"]["SettlementView"];

/** D-41: the invoice's settlement, or `null` when none has been recorded (the API's 404). */
export async function getSettlement(id: string): Promise<Settlement | null> {
  const client = await billing();
  const result = await client.GET("/api/billing/admin/invoices/{id}/settlement", { params: { path: { id } } });
  if (result.response.status === 404) return null;
  return unwrap(Promise.resolve(result)) as Promise<Settlement>;
}

/** D-41: settles a final B2B invoice after payment. The invoice and its GST do not change. */
export async function settleInvoice(id: string, body: { discount: number; roundOff: number; reason: string }): Promise<Settlement> {
  return unwrap(
    (await billing()).POST("/api/billing/admin/invoices/{id}/settlement", { params: { path: { id } }, body }),
  ) as Promise<Settlement>;
}

export async function cancelInvoice(id: string, reason: string): Promise<void> {
  await unwrap(
    (await billing()).POST("/api/billing/admin/invoices/{id}/cancel", {
      params: { path: { id } },
      body: { reason },
    }),
  );
}

export type CreditNoteLine = { invoiceLineId: string; quantity: string };

/**
 * The created credit note's URL key, from the 201's `CreditNoteResponse` — its number, or its id
 * when the body carries none (the detail route redirects an id to the number URL).
 */
export async function issueCreditNote(invoiceId: string, reason: string, lines: CreditNoteLine[]): Promise<string> {
  const body = await unwrap(
    (await billing()).POST("/api/billing/admin/invoices/{id}/credit-notes", {
      params: { path: { id: invoiceId } },
      // Quantities are typed as entered; the document types a number (a parse, not arithmetic).
      body: { reason, lines: lines.map((line) => ({ invoiceLineId: line.invoiceLineId, quantity: Number(line.quantity) })) },
    }),
  );
  const id = text((body as Record<string, unknown> | undefined)?.id);
  if (id === null) {
    throw new Error(
      "POST /api/billing/admin/invoices/{id}/credit-notes answered 201 with no `id` on the body. " +
        "That is a contract change, not a credit note this screen can navigate to (constitution VI).",
    );
  }
  return text((body as Record<string, unknown>).creditNoteNumber) ?? id;
}

export type CreditNoteDetailLine = {
  id: string;
  invoiceLineId: string;
  skuName: string;
  hsnCode: string;
  gstRate: number | string;
  unitCode: string;
  quantity: number | string;
  rate: number | string;
  isGstInclusive: boolean;
  taxableValue: number;
  cgst: number;
  sgst: number;
  igst: number;
  taxAmount: number;
  lineTotal: number;
  /** D-39: returned quantity × the invoiced rate, before the invoice's trade discount. */
  grossAmount: number;
  discountPercent: number;
  /** D-39: the returned goods' share of the trade discount, credited back at the price charged. */
  discount: number;
};

export type CreditNoteDetail = {
  id: string;
  invoiceId: string;
  creditNoteNumber: string;
  financialYear: string;
  customerId: string;
  issuedOn: string;
  reason: string;
  taxHead: string;
  placeOfSupply: string;
  lines: CreditNoteDetailLine[];
  grossValue: number;
  discount: number;
  taxableValue: number;
  cgst: number;
  sgst: number;
  igst: number;
  roundOff: number;
  grandTotal: number;
  irn: string | null;
  irnStatus: string;
  tallySyncStatus: string;
  /** The parties and the document's own display figures, as Billing recorded them — `null` when absent. */
  invoiceNumber: string | null;
  supplierGstin: string | null;
  supplierLegalName: string | null;
  supplierAddress: string | null;
  recipientGstin: string | null;
  recipientLegalName: string | null;
  recipientAddress: string | null;
  grandTotalDisplay: string | null;
  irnQr: string | null;
};

/**
 * `AdminInvoiceController.GetCreditNote` — the read side of a raised credit note, projected the
 * same way `GetInvoice` projects an invoice (a read and a render, no arithmetic). Validated at the
 * boundary like `parseInvoicePrint` (constitution VI): a field missing at runtime is a contract
 * change, not a document to render half-empty.
 */
function parseCreditNoteDetail(body: unknown): CreditNoteDetail {
  const raw = (body ?? {}) as Record<string, unknown>;
  const id = text(raw.id);
  const invoiceId = text(raw.invoiceId);
  const creditNoteNumber = text(raw.creditNoteNumber);
  const financialYear = text(raw.financialYear);
  const customerId = text(raw.customerId);
  const issuedOn = text(raw.issuedOn);
  const reason = text(raw.reason);
  const taxHead = text(raw.taxHead);
  const placeOfSupply = text(raw.placeOfSupply);
  const irnStatus = text(raw.irnStatus);
  const tallySyncStatus = text(raw.tallySyncStatus);
  const lines = raw.lines;

  if (
    id === null ||
    invoiceId === null ||
    creditNoteNumber === null ||
    financialYear === null ||
    customerId === null ||
    issuedOn === null ||
    reason === null ||
    taxHead === null ||
    placeOfSupply === null ||
    irnStatus === null ||
    tallySyncStatus === null ||
    !Array.isArray(lines)
  ) {
    throw new Error(
      "GET /api/billing/admin/credit-notes/{id} answered with a body this screen cannot read " +
        "(a required field or the lines array is missing). That is a contract change, not a document " +
        "to render half-empty (constitution VI).",
    );
  }

  return {
    id,
    invoiceId,
    creditNoteNumber,
    financialYear,
    customerId,
    issuedOn,
    reason,
    taxHead,
    placeOfSupply,
    lines: lines.map((line) => {
      const l = (line ?? {}) as Record<string, unknown>;
      return {
        id: text(l.id) ?? "",
        invoiceLineId: text(l.invoiceLineId) ?? "",
        skuName: text(l.skuName) ?? "",
        hsnCode: text(l.hsnCode) ?? "",
        gstRate: (decimal(l.gstRate) ?? l.gstRate) as number | string,
        unitCode: text(l.unitCode) ?? "",
        quantity: (decimal(l.quantity) ?? l.quantity) as number | string,
        rate: (decimal(l.rate) ?? l.rate) as number | string,
        isGstInclusive: l.isGstInclusive === true,
        taxableValue: decimal(l.taxableValue) ?? 0,
        cgst: decimal(l.cgst) ?? 0,
        sgst: decimal(l.sgst) ?? 0,
        igst: decimal(l.igst) ?? 0,
        taxAmount: decimal(l.taxAmount) ?? 0,
        lineTotal: decimal(l.lineTotal) ?? 0,
        grossAmount: decimal(l.grossAmount) ?? decimal(l.taxableValue) ?? 0,
        discountPercent: decimal(l.discountPercent) ?? 0,
        discount: decimal(l.discount) ?? 0,
      };
    }),
    grossValue: decimal(raw.grossValue) ?? decimal(raw.taxableValue) ?? 0,
    discount: decimal(raw.discount) ?? 0,
    taxableValue: decimal(raw.taxableValue) ?? 0,
    cgst: decimal(raw.cgst) ?? 0,
    sgst: decimal(raw.sgst) ?? 0,
    igst: decimal(raw.igst) ?? 0,
    roundOff: decimal(raw.roundOff) ?? 0,
    grandTotal: decimal(raw.grandTotal) ?? 0,
    irn: text(raw.irn),
    irnStatus,
    tallySyncStatus,
    invoiceNumber: text(raw.invoiceNumber),
    supplierGstin: text(raw.supplierGstin),
    supplierLegalName: text(raw.supplierLegalName),
    supplierAddress: text(raw.supplierAddress),
    recipientGstin: text(raw.recipientGstin),
    recipientLegalName: text(raw.recipientLegalName),
    recipientAddress: text(raw.recipientAddress),
    grandTotalDisplay: text(raw.grandTotalDisplay),
    irnQr: text(raw.irnQr),
  };
}

export async function getCreditNote(creditNoteId: string): Promise<CreditNoteDetail> {
  return parseCreditNoteDetail(
    await unwrap(
      (await billing()).GET("/api/billing/admin/credit-notes/{creditNoteId}", {
        params: { path: { creditNoteId } },
      }),
    ),
  );
}

export async function getCreditNoteByNumber(number: string): Promise<CreditNoteDetail> {
  return parseCreditNoteDetail(
    await unwrap((await billing()).GET("/api/billing/admin/credit-notes/by-number", { params: { query: { number } } })),
  );
}

/**
 * The credit note a route's `:creditNoteNumber` names; a legacy id throws a redirect to the number
 * URL unless that number is ambiguous (then the id URL stays). An ambiguous number throws
 * `AmbiguousNumberError` with no matches: the admin has no credit-note list to find them in.
 */
export async function creditNoteByKey(request: Request, key: string): Promise<CreditNoteDetail> {
  if (!isGuid(key)) {
    try {
      return await getCreditNoteByNumber(key);
    } catch (err) {
      if (isConflict(err)) throw new AmbiguousNumberError(err, []);
      throw err;
    }
  }
  const note = await getCreditNote(key);
  if (await numberIsAmbiguous(() => getCreditNoteByNumber(note.creditNoteNumber))) return note;
  throw redirectToCanonical(request, key, creditNotePath(note.creditNoteNumber));
}

export async function changeSeriesFormat(id: string, format: string): Promise<void> {
  await unwrap(
    (await billing()).PUT("/api/billing/admin/invoice-series/{id}", {
      params: { path: { id } },
      body: { format },
    }),
  );
}

/** Only a series that has never issued a number (`nextNumber` 1); one that has is a `422`. */
export async function deleteInvoiceSeries(id: string): Promise<void> {
  await unwrap((await billing()).DELETE("/api/billing/admin/invoice-series/{id}", { params: { path: { id } } }));
}

/** Year and format are optional; the server defaults them to the current financial year and the configured format. */
export async function createInvoiceSeries(body: { seriesCode: string; financialYear?: string; format?: string }): Promise<void> {
  await unwrap(
    (await billing()).POST("/api/billing/admin/invoice-series", {
      body: { seriesCode: body.seriesCode, financialYear: body.financialYear ?? null, format: body.format ?? null },
    }),
  );
}

// ---- 008-reporting-dashboards T023 (US4, FR-015). `GET /api/billing/reports/sales`, the same
// ReportController this file's GstSummary caller already reads. Types come straight from the
// generated client rather than being retyped by hand here (`ReportController.cs:87-158` verified
// against the running gateway 2026-09-09): `groupBy` is a bare C# enum with no
// JsonStringEnumConverter registered for Billing's controllers (unlike Catalog/Customers), so it
// serialises as the plain int the generated type already says — confirmed live, not guessed. ----

export type SalesReport = BillingComponents["schemas"]["SalesReport"];
export type SalesRow = BillingComponents["schemas"]["SalesRow"];

/** `SalesGrouping.cs`: Brand=0, Category=1, Customer=2, Sku=3, Tier=4 — display label only. */
const SALES_GROUPING_LABEL: Readonly<Record<number, string>> = {
  0: "Brand",
  1: "Category",
  2: "Customer",
  3: "SKU",
  4: "Tier",
};

export function salesGroupingLabel(groupBy: number | string): string {
  return SALES_GROUPING_LABEL[Number(groupBy)] ?? String(groupBy);
}

const SALES_QUANTITY = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 3 });

/** Formatting, not calculation — renders whatever the API sent (constitution I). */
export function salesQuantity(value: unknown): string {
  const parsed = decimal(value);
  return parsed === null ? "—" : SALES_QUANTITY.format(parsed);
}

export async function getSalesReport(params: { from: string; to: string; groupBy?: string; top?: number }): Promise<SalesReport> {
  return unwrap(
    (await billing()).GET("/api/billing/reports/sales", { params: { query: params } }),
  );
}

/**
 * The dashboard's invoiced trend — `GET /api/billing/reports/sales-trend` (backend invoicing-admin.md
 * §9). Every day or month in the period, zero-filled by the API, with `totals` the API summed: the
 * dashboard reads both as-is and adds nothing up (constitution I).
 */
export type SalesTrendReport = BillingComponents["schemas"]["SalesTrendReport"];

export async function getSalesTrend(params: { from: string; to: string; bucket: "day" | "month" }): Promise<SalesTrendReport> {
  return unwrap(
    (await billing()).GET("/api/billing/reports/sales-trend", { params: { query: params } }),
  );
}

/**
 * §17 Integrations "error log", Billing's half: invoices whose IRN or e-way bill integration still
 * stands failed, most recently moved first, with the GSP's / e-way portal's own text
 * (`GET /api/billing/admin/integration-errors`, `integrations.read`).
 */
export type IntegrationErrorRow = BillingComponents["schemas"]["IntegrationErrorRow"];

export async function listIntegrationErrors(params: { page?: number; pageSize?: number } = {}) {
  return unwrap((await billing()).GET("/api/billing/admin/integration-errors", { params: { query: params } }));
}

/**
 * §17 Integrations "GSP and gateway configuration": what the next payment, registration and e-way
 * bill will use, and what is registered, plus every value behind it — a secret only as "set or not",
 * never its value (`GET /api/billing/admin/integration-settings`). Changing it is
 * `PUT` with the keys that changed (`integrations.administer`); the server applies it live.
 */
export type IntegrationSettings = BillingComponents["schemas"]["IntegrationSettingsView"];

export async function getIntegrationSettings(): Promise<IntegrationSettings> {
  return unwrap((await billing()).GET("/api/billing/admin/integration-settings"));
}

/** Only the keys that changed: an empty value drops that override (back to configuration), a key left out is untouched. */
export async function saveIntegrationSettings(values: Record<string, string>): Promise<IntegrationSettings> {
  return unwrap((await billing()).PUT("/api/billing/admin/integration-settings", { body: { values } }));
}
